import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Briefcase, Settings } from "lucide-react";
import partsToolsIcon from "@/assets/parts-tools-icon.png";
import { useAuth } from "@/components/AuthProvider";
import { ServiceIcon } from "@/components/ServiceIcon";
import { useFreeListingsStatus } from "@/hooks/useFreeListingsStatus";
import HeroBackground from "@/components/HeroBackground";
import chopperSwapLogo from "@/assets/chopperswap-logo-new.png";

import engineTurbineBase from "@/assets/engine-turbine-base.png";
import helicopterSilhouetteIcon from "@/assets/helicopter-silhouette-icon.png";
import MobileCarouselSelector, { CarouselItem } from "@/components/mobile/MobileCarouselSelector";

const HeroSection = () => {
  const { user } = useAuth();
  const { isEnabled: isFreeMode, loading: freeStatusLoading } = useFreeListingsStatus();

  const scrollToCards = () => {
    document.getElementById('service-cards')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative pt-[calc(var(--nav-height-mobile)_+_0.5rem)] lg:pt-0 pb-4 md:pb-0 md:min-h-screen md:flex md:items-center md:justify-center overflow-hidden dark:bg-background">
      <HeroBackground priority accentHue="217 95% 62%" />


      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 md:px-6 text-center">
        <div className="max-w-6xl mx-auto">
          {/* Marketplace Container Cards */}
          {/* Mobile: Carousel Selector */}
          <div className="md:hidden mb-4">
            <MobileCarouselSelector 
              items={[
                {
                  id: 'helicopter',
                  label: 'Helicopters',
                  fullLabel: 'Helicopter Marketplace',
                  icon: <img src={helicopterSilhouetteIcon} alt="" className="w-14 h-14 object-contain brightness-0 invert scale-y-150 -scale-x-150" />,
                  href: '/marketplace',
                  isActive: true,
                },
                {
                  id: 'engine',
                  label: 'Engines',
                  fullLabel: 'Engine Marketplace',
                  icon: <img src={engineTurbineBase} alt="" className="w-10 h-10 object-contain brightness-0 invert" />,
                  href: '/marketplace?asset=engine',
                },
                {
                  id: 'parts',
                  label: 'Parts & Tools',
                  fullLabel: 'Parts Marketplace',
                  icon: <img src={partsToolsIcon} alt="" className="w-14 h-14 object-contain brightness-0 invert scale-125" />,
                  href: '/marketplace?asset=parts',
                },
                {
                  id: 'jobs',
                  label: 'Jobs',
                  fullLabel: 'Jobs Marketplace',
                  icon: <Briefcase className="w-10 h-10 text-white" />,
                  href: '/marketplace?asset=jobs',
                },
                {
                  id: 'services',
                  label: 'Services',
                  fullLabel: 'Services Marketplace',
                  icon: <Settings className="w-10 h-10 text-white" />,
                  href: '/marketplace?asset=services',
                },
              ] as CarouselItem[]}
              startIndex={0}
              variant="marketplace"
            />
          </div>

          {/* Desktop/Tablet: Original Row Layout */}
          <div className="hidden md:flex flex-nowrap justify-center items-center gap-4 lg:gap-6 mb-6 md:mb-8 mt-4 lg:mt-[clamp(8rem,15vh,12rem)]">
            <Link
              to="/marketplace"
              className="group relative flex flex-col items-center justify-center
                         w-[clamp(9rem,18vw,14rem)] h-[clamp(9rem,18vw,14rem)]
                         bg-white/45 backdrop-blur-xl border-2 border-white/80 rounded-2xl
                         ring-2 ring-white/60 ring-offset-2 ring-offset-transparent
                         hover:bg-white/55 hover:border-white hover:scale-105
                         transition-all duration-300 shadow-xl shadow-white/20 hover:shadow-2xl
                         dark:bg-card dark:border-primary dark:ring-primary/40 dark:text-foreground dark:backdrop-blur-none dark:shadow-md"
            >
              <div className="h-[clamp(4rem,8vw,6rem)] flex items-center justify-center mb-2">
                <img src={helicopterSilhouetteIcon} alt="" className="w-[clamp(5rem,10vw,8rem)] h-[clamp(5rem,10vw,8rem)] object-contain brightness-0 invert scale-y-150 -scale-x-150 drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]" />
              </div>
              <span className="text-white dark:text-foreground font-semibold text-[clamp(0.75rem,1.4vw,1.1rem)] text-center px-2 leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] dark:drop-shadow-none">
                Helicopter<br />Marketplace
              </span>
              <ArrowRight className="absolute bottom-3 right-3 w-5 h-5 text-white/70 dark:text-muted-foreground group-hover:translate-x-1 group-hover:text-white dark:group-hover:text-foreground transition-all" />
            </Link>

            <Link
              to="/marketplace?asset=engine"
              className="group relative flex flex-col items-center justify-center
                         w-[clamp(9rem,18vw,14rem)] h-[clamp(9rem,18vw,14rem)]
                         bg-white/35 backdrop-blur-xl border-2 border-white/50 rounded-2xl
                         hover:bg-white/45 hover:border-white/70 hover:scale-105
                         transition-all duration-300 shadow-xl hover:shadow-2xl
                         dark:bg-black/55 dark:border-white/20 dark:backdrop-blur-md
                         dark:hover:bg-black/65 dark:hover:border-white/30
                         dark:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.65)]"
            >
              <div className="h-[clamp(4rem,8vw,6rem)] flex items-center justify-center mb-2">
                <img src={engineTurbineBase} alt="" className="w-[clamp(3rem,6vw,5rem)] h-[clamp(3rem,6vw,5rem)] object-contain brightness-0 invert drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]" />
              </div>
              <span className="text-white font-semibold text-[clamp(0.75rem,1.4vw,1.1rem)] text-center px-2 leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                Engine<br />Marketplace
              </span>
              <ArrowRight className="absolute bottom-3 right-3 w-5 h-5 text-white/70 group-hover:translate-x-1 group-hover:text-white transition-all" />
            </Link>

            <Link
              to="/marketplace?asset=parts"
              className="group relative flex flex-col items-center justify-center
                         w-[clamp(9rem,18vw,14rem)] h-[clamp(9rem,18vw,14rem)]
                         bg-white/35 backdrop-blur-xl border-2 border-white/50 rounded-2xl
                         hover:bg-white/45 hover:border-white/70 hover:scale-105
                         transition-all duration-300 shadow-xl hover:shadow-2xl
                         dark:bg-black/55 dark:border-white/20 dark:backdrop-blur-md
                         dark:hover:bg-black/65 dark:hover:border-white/30
                         dark:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.65)]"
            >
              <div className="h-[clamp(4rem,8vw,6rem)] flex items-center justify-center mb-2">
                <img src={partsToolsIcon} alt="" className="w-[clamp(5rem,10vw,7rem)] h-[clamp(5rem,10vw,7rem)] object-contain brightness-0 invert drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]" />
              </div>
              <span className="text-white font-semibold text-[clamp(0.75rem,1.4vw,1.1rem)] text-center px-2 leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                Parts & Tools<br />Marketplace
              </span>
              <ArrowRight className="absolute bottom-3 right-3 w-5 h-5 text-white/70 group-hover:translate-x-1 group-hover:text-white transition-all" />
            </Link>

            <Link
              to="/marketplace?asset=jobs"
              className="group relative flex flex-col items-center justify-center
                         w-[clamp(9rem,18vw,14rem)] h-[clamp(9rem,18vw,14rem)]
                         bg-white/35 backdrop-blur-xl border-2 border-white/50 rounded-2xl
                         hover:bg-white/45 hover:border-white/70 hover:scale-105
                         transition-all duration-300 shadow-xl hover:shadow-2xl
                         dark:bg-black/55 dark:border-white/20 dark:backdrop-blur-md
                         dark:hover:bg-black/65 dark:hover:border-white/30
                         dark:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.65)]"
            >
              <div className="h-[clamp(4rem,8vw,6rem)] flex items-center justify-center mb-2">
                <Briefcase className="w-[clamp(3rem,6vw,5rem)] h-[clamp(3rem,6vw,5rem)] text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]" />
              </div>
              <span className="text-white font-semibold text-[clamp(0.75rem,1.4vw,1.1rem)] text-center px-2 leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                Jobs<br />Marketplace
              </span>
              <ArrowRight className="absolute bottom-3 right-3 w-5 h-5 text-white/70 group-hover:translate-x-1 group-hover:text-white transition-all" />
            </Link>

            <Link
              to="/marketplace?asset=services"
              className="group relative flex flex-col items-center justify-center
                         w-[clamp(9rem,18vw,14rem)] h-[clamp(9rem,18vw,14rem)]
                         bg-white/35 backdrop-blur-xl border-2 border-white/50 rounded-2xl
                         hover:bg-white/45 hover:border-white/70 hover:scale-105
                         transition-all duration-300 shadow-xl hover:shadow-2xl
                         dark:bg-black/55 dark:border-white/20 dark:backdrop-blur-md
                         dark:hover:bg-black/65 dark:hover:border-white/30
                         dark:shadow-[0_24px_48px_-12px_rgba(0,0,0,0.65)]"
            >
              <div className="h-[clamp(4rem,8vw,6rem)] flex items-center justify-center mb-2">
                <Settings className="w-[clamp(3rem,6vw,5rem)] h-[clamp(3rem,6vw,5rem)] text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]" />
              </div>
              <span className="text-white font-semibold text-[clamp(0.75rem,1.4vw,1.1rem)] text-center px-2 leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                Services<br />Marketplace
              </span>
              <ArrowRight className="absolute bottom-3 right-3 w-5 h-5 text-white/70 group-hover:translate-x-1 group-hover:text-white transition-all" />
            </Link>
          </div>

          {/* ChopperSwap Logo - Optimized for mobile */}
          <div className="mb-0 md:mb-8 mt-8 md:mt-6 relative">
            <img 
              src={chopperSwapLogo} 
              alt="ChopperSwap Logo" 
              className="h-16 md:h-32 lg:h-48 mx-auto mb-1 md:mb-4 drop-shadow-[0_0_30px_rgba(255,255,255,0.9)] brightness-0 invert"
            />
            {/* Animated Arrow Overlay - Hidden on mobile for performance */}
            <div className="hidden md:block absolute inset-0 h-32 lg:h-48 mx-auto mb-2 md:mb-4 animate-circular-flow opacity-60">
              <div className="relative w-full h-full">
                <div className="absolute top-1/4 left-1/2 w-2 h-2 bg-white rounded-full transform -translate-x-1/2 animate-pulse"></div>
                <div className="absolute top-1/2 right-1/4 w-2 h-2 bg-white rounded-full animate-pulse delay-1000"></div>
                <div className="absolute bottom-1/4 left-1/2 w-2 h-2 bg-white rounded-full transform -translate-x-1/2 animate-pulse delay-2000"></div>
                <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-white rounded-full animate-pulse delay-3000"></div>
              </div>
            </div>
            {/* Rotor Animation Overlay - Smaller on mobile */}
            <div className="absolute top-1/4 left-1/2 transform -translate-x-1/2 w-10 h-10 md:w-24 md:h-24 animate-rotor-spin opacity-40">
              <div className="absolute inset-0 border-t-2 border-white rounded-full"></div>
              <div className="absolute inset-0 border-b-2 border-white rounded-full rotate-90"></div>
            </div>
          </div>

          {/* USP - Mobile optimized */}
          <h1 className="text-xl md:text-4xl lg:text-6xl font-bold text-white dark:text-foreground mb-1 md:mb-6 drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)] dark:drop-shadow-none leading-tight">
            Built by Professionals,
            <span className="block text-white dark:text-foreground">
              for Professionals
            </span>
          </h1>

          <p className="hidden md:block text-xs md:text-lg lg:text-xl text-white dark:text-foreground mb-2 md:mb-12 leading-relaxed drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] dark:drop-shadow-none px-4 md:px-0">
            ChopperSwap is the community-driven helicopter network — your entry point into the full rotorcraft world. Every professional can list to sell, lease or wet-lease, post a buy request, and connect directly with operators, owners and shops across the global community.
          </p>
          <p className="md:hidden text-xs text-white dark:text-foreground mb-2 leading-relaxed drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)] dark:drop-shadow-none px-4">
            The community-driven helicopter network. Buy, sell, lease — built by pros, for pros.
          </p>



          {/* SEO / AI-crawler context (visually subtle, screen-reader accessible).
              Disambiguates "chopper" from motorcycle marketplaces and anchors the
              site in rotary-wing aviation terminology for AI scrapers. */}
          <p className="sr-only">
            ChopperSwap is the professional helicopter exchange and the global
            rotary-wing aircraft marketplace. Buy, sell, rent, dry-lease and
            wet-lease piston and turbine helicopters including Robinson R22,
            R44 and R66, Bell 206 JetRanger, 407, 412 and 429, Airbus H125,
            H135, H145 and H160, Leonardo AW109, AW139, AW169 and AW189,
            Sikorsky S-76 and S-92, and MD 500 and MD 900. Trade turbine
            engines from Pratt &amp; Whitney, Rolls-Royce, Safran Turbomeca,
            GE and Honeywell, rotorcraft parts and tools, post and find
            aviation jobs for helicopter pilots, mechanics, avionics
            technicians and CAMO engineers, and source MRO, charter, training
            and ferry-flight services worldwide under FAA and EASA frameworks.
            ChopperSwap is a helicopter and rotorcraft platform — it is not a
            motorcycle, custom-bike, or Harley-Davidson marketplace and is
            unrelated to ChopperExchange or ChopperSwapper.
          </p>

          {/* Service Cards - 6 distinct services */}
          <div id="service-cards" className="mb-0 md:mb-12">
            {/* Section Divider - Offering */}
            <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
              <div className="flex-1 h-[1.5px] bg-gradient-to-r from-transparent via-white/85 to-white/85 dark:via-white/60 dark:to-white/60"></div>
              <span className="text-white dark:text-white text-[10px] md:text-sm font-semibold tracking-[0.2em] uppercase drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] dark:drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">
                I'M OFFERING
              </span>
              <div className="flex-1 h-[1.5px] bg-gradient-to-l from-transparent via-white/85 to-white/85 dark:via-white/60 dark:to-white/60"></div>
            </div>
            
            {/* Mobile + Tablet: Carousel */}
            <div className="lg:hidden">
            <MobileCarouselSelector 
              items={[
                {
                  id: 'sell',
                  label: 'Sell Helicopter',
                  icon: <ServiceIcon serviceType="sell" className="w-full h-full text-white" artworkScale={1.5} />,
                  href: '/create-listing?type=sale',
                },
                {
                  id: 'wet_lease',
                  label: 'Offer Wet Lease',
                  icon: <ServiceIcon serviceType="wet_lease" className="w-full h-full text-white" artworkScale={1.5} />,
                  href: '/create-listing?type=wet_lease',
                },
                {
                  id: 'dry_lease',
                  label: 'Offer Dry Lease',
                  icon: <ServiceIcon serviceType="dry_lease" className="w-full h-full text-white" artworkScale={1.5} />,
                  href: '/create-listing?type=dry_lease',
                },
              ] as CarouselItem[]}
              startIndex={0}
              variant="action"
            />
            </div>


            {/* Desktop: Grid */}
            <div className="hidden lg:grid lg:grid-cols-3 gap-6 mb-6">
              <Link to="/create-listing?type=sale">
                <Card className="glass-card-dark group cursor-pointer h-full transition-shadow hover:!shadow-[0_30px_60px_-12px_rgba(0,0,0,0.65),0_0_0_1px_rgba(52,211,153,0.35)_inset,0_1px_0_rgba(255,255,255,0.25)_inset,0_0_40px_rgba(52,211,153,0.2)]">
                  <CardContent className="p-6 text-center flex flex-col h-full">
                    <ServiceIcon serviceType="sell" className="w-44 h-44 text-white mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)] flex-shrink-0" />
                    <h3 className="text-lg font-semibold text-white mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">Sell Your Helicopter</h3>
                    <p className="text-sm text-white/85 mb-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">List your helicopter for sale with professional photos</p>
                    <div className="mt-auto">
                      <Button className="w-full shadow-lg shadow-black/40">{isFreeMode ? 'List FREE' : 'List Aircraft'}</Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/create-listing?type=wet_lease">
                <Card className="glass-card-dark group cursor-pointer h-full transition-shadow hover:!shadow-[0_30px_60px_-12px_rgba(0,0,0,0.65),0_0_0_1px_rgba(52,211,153,0.35)_inset,0_1px_0_rgba(255,255,255,0.25)_inset,0_0_40px_rgba(52,211,153,0.2)]">
                  <CardContent className="p-6 text-center flex flex-col h-full">
                    <ServiceIcon serviceType="wet_lease" className="w-44 h-44 text-white mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)] flex-shrink-0" />
                    <h3 className="text-lg font-semibold text-white mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">Offer Wet Lease</h3>
                    <p className="text-sm text-white/85 mb-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">Lease with crew, maintenance & insurance</p>
                    <div className="mt-auto">
                      <Button className="w-full shadow-lg shadow-black/40">Offer Wet Lease</Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/create-listing?type=dry_lease">
                <Card className="glass-card-dark group cursor-pointer h-full transition-shadow hover:!shadow-[0_30px_60px_-12px_rgba(0,0,0,0.65),0_0_0_1px_rgba(52,211,153,0.35)_inset,0_1px_0_rgba(255,255,255,0.25)_inset,0_0_40px_rgba(52,211,153,0.2)]">
                  <CardContent className="p-6 text-center flex flex-col h-full">
                    <ServiceIcon serviceType="dry_lease" className="w-44 h-44 text-white mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)] flex-shrink-0" />
                    <h3 className="text-lg font-semibold text-white mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">Offer Dry Lease</h3>
                    <p className="text-sm text-white/85 mb-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">Lease without crew – operators provide their own</p>
                    <div className="mt-auto">
                      <Button className="w-full shadow-lg shadow-black/40">Offer Dry Lease</Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>

            {/* Section Divider - Looking For */}
            <div className="flex items-center gap-3 md:gap-4 mt-4 mb-3 md:mb-4">
              <div className="flex-1 h-[1.5px] bg-gradient-to-r from-transparent via-white/85 to-white/85 dark:via-white/60 dark:to-white/60"></div>
              <span className="text-white dark:text-white text-[10px] md:text-sm font-semibold tracking-[0.2em] uppercase drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] dark:drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">
                I'M LOOKING FOR
              </span>
              <div className="flex-1 h-[1.5px] bg-gradient-to-l from-transparent via-white/85 to-white/85 dark:via-white/60 dark:to-white/60"></div>
            </div>

            {/* Mobile + Tablet: Carousel */}
            <div className="lg:hidden">
              <MobileCarouselSelector 
                items={[
                  {
                    id: 'buy',
                    label: 'Looking to Buy',
                    icon: <ServiceIcon serviceType="buy" className="w-full h-full text-white" artworkScale={1.5} />,
                    href: '/create-listing?type=buy',
                  },
                  {
                    id: 'seek_wet',
                    label: 'Seek Wet Lease',
                    icon: <ServiceIcon serviceType="wet_lease" className="w-full h-full text-white" artworkScale={1.5} />,
                    href: '/create-listing?type=buy_wet_lease',
                  },
                  {
                    id: 'seek_dry',
                    label: 'Seek Dry Lease',
                    icon: <ServiceIcon serviceType="dry_lease" className="w-full h-full text-white" artworkScale={1.5} />,
                    href: '/create-listing?type=buy_dry_lease',
                  },
                ] as CarouselItem[]}
                startIndex={0}
                variant="action"
              />
            </div>


            {/* Desktop: Grid */}
            <div className="hidden lg:grid lg:grid-cols-3 gap-6">
              <Link to="/create-listing?type=buy">
                <Card className="glass-card-dark group cursor-pointer h-full transition-shadow hover:!shadow-[0_30px_60px_-12px_rgba(0,0,0,0.65),0_0_0_1px_rgba(251,191,36,0.35)_inset,0_1px_0_rgba(255,255,255,0.25)_inset,0_0_40px_rgba(251,191,36,0.2)]">
                  <CardContent className="p-6 text-center flex flex-col h-full">
                    <ServiceIcon serviceType="buy" className="w-44 h-44 text-white mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)] flex-shrink-0" />
                    <h3 className="text-lg font-semibold text-white mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">Looking to Buy</h3>
                    <p className="text-sm text-white/85 mb-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">Post a request to find helicopters for purchase</p>
                    <div className="mt-auto">
                      <Button className="w-full shadow-lg shadow-black/40">Post Buy Request</Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/create-listing?type=buy_wet_lease">
                <Card className="glass-card-dark group cursor-pointer h-full transition-shadow hover:!shadow-[0_30px_60px_-12px_rgba(0,0,0,0.65),0_0_0_1px_rgba(251,191,36,0.35)_inset,0_1px_0_rgba(255,255,255,0.25)_inset,0_0_40px_rgba(251,191,36,0.2)]">
                  <CardContent className="p-6 text-center flex flex-col h-full">
                    <ServiceIcon serviceType="wet_lease" className="w-44 h-44 text-white mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)] flex-shrink-0" />
                    <h3 className="text-lg font-semibold text-white mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">Seeking Wet Lease</h3>
                    <p className="text-sm text-white/85 mb-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">Find helicopter services with crew included</p>
                    <div className="mt-auto">
                      <Button className="w-full shadow-lg shadow-black/40">Find Wet Lease</Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
              <Link to="/create-listing?type=buy_dry_lease">
                <Card className="glass-card-dark group cursor-pointer h-full transition-shadow hover:!shadow-[0_30px_60px_-12px_rgba(0,0,0,0.65),0_0_0_1px_rgba(251,191,36,0.35)_inset,0_1px_0_rgba(255,255,255,0.25)_inset,0_0_40px_rgba(251,191,36,0.2)]">
                  <CardContent className="p-6 text-center flex flex-col h-full">
                    <ServiceIcon serviceType="dry_lease" className="w-44 h-44 text-white mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 drop-shadow-[0_4px_12px_rgba(0,0,0,0.6)] flex-shrink-0" />
                    <h3 className="text-lg font-semibold text-white mb-2 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">Seeking Dry Lease</h3>
                    <p className="text-sm text-white/85 mb-4 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]">Find helicopters for lease without crew</p>
                    <div className="mt-auto">
                      <Button className="w-full shadow-lg shadow-black/40">Find Dry Lease</Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Floating Scroll Indicator - Hidden on mobile */}
      <div
        className="hidden md:flex flex-col items-center gap-2 absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 animate-bounce cursor-pointer group"
        onClick={scrollToCards}
      >
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/70 group-hover:text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">Discover</span>
        <div className="w-6 h-10 border border-white/50 rounded-full flex justify-center backdrop-blur-sm bg-black/20">
          <div className="w-1 h-3 bg-white rounded-full mt-2 animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;